import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ApiError } from "../api/errors";
import { AuthProvider, useAuth } from "../auth/AuthProvider";
import { CAP } from "../auth/capabilities";
import { ChangePasswordPage } from "../auth/ChangePasswordPage";
import { LoginPage } from "../auth/LoginPage";
import {
  CHANGE_PASSWORD_PATH,
  RequireAuth,
  RequireCapability,
} from "../auth/RequireAuth";
import { BulkProductsPage } from "../bulk/BulkProductsPage";
import { DashboardHome } from "../dashboard/DashboardHome";
import { DiscountsPage } from "../discounts/DiscountsPage";
import { InventoryPage } from "../inventory/InventoryPage";
import { OrderDetailPage } from "../orders/OrderDetailPage";
import { OrdersPage } from "../orders/list/OrdersPage";
import { AddProductPage } from "../products/add/AddProductPage";
import { EditProductPage } from "../products/edit/EditProductPage";
import { ProductManagementPage } from "../products/list/ProductManagementPage";
import { ProductWorkspacePage } from "../products/ProductWorkspacePage";
import { UsersPage } from "../users/UsersPage";
import { Shell } from "./Shell";

/** A client whose defaults suit a lifecycle tool.
 *
 * Mutations never retry: a publish that appears to have failed may have
 * succeeded, and repeating it is how a product gets published twice. Queries
 * retry once for a genuine network blip but never for an authentication or
 * conflict answer, which retrying cannot fix.
 */
function createQueryClient(onUnauthenticated: () => void): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (error instanceof ApiError && error.kind === "unauthenticated") {
          onUnauthenticated();
        }
      },
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.kind === "network") {
            return failureCount < 1;
          }
          return false;
        },
        refetchOnWindowFocus: false,
        staleTime: 10_000,
      },
      mutations: { retry: false },
    },
  });
}

/** Where an administrator lands when they ask for the root.
 *
 * The Dashboard summary reads orders, stock and the catalogue at once, so it is
 * `dashboard.read` and currently an owner's alone. Sending everybody else there
 * would open the application on a screen that answers 403 — the first thing a
 * new catalogue editor would ever see. So the root resolves to the first
 * section this administrator may actually stand in.
 *
 * The order is the order the work happens in, and it is deliberately short: the
 * point is a deterministic answer, not a preference engine.
 */
function LandingRoute() {
  const { can } = useAuth();

  if (can(CAP.dashboardRead)) return <DashboardHome />;
  if (can(CAP.productsRead)) return <Navigate to="/products" replace />;
  if (can(CAP.ordersRead)) return <Navigate to="/orders" replace />;
  if (can(CAP.inventoryRead)) return <Navigate to="/inventory" replace />;
  if (can(CAP.usersRead)) return <Navigate to="/users" replace />;

  // An account whose roles were all revoked. It can still sign out and change
  // its password, and it is told the truth rather than bounced between screens
  // that all refuse it.
  return (
    <Shell title="OptiQ Admin">
      <div className="panel">
        <div className="empty" role="status">
          Your account has no sections yet. Ask an owner to give it a role.
        </div>
      </div>
    </Shell>
  );
}

function AuthedApp() {
  const { clearSession } = useAuth();
  const client = useMemo(() => createQueryClient(clearSession), [clearSession]);

  return (
    <QueryClientProvider client={client}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* The one authenticated screen behind no capability at all. An account
            holding a temporary password is refused every other route by the
            backend, so this has to be reachable or the account is stranded. */}
        <Route
          path={CHANGE_PASSWORD_PATH}
          element={
            <RequireAuth>
              <ChangePasswordPage />
            </RequireAuth>
          }
        />
        {/* The dashboard is the landing screen for an administrator who may
            read it; everybody else lands on the first section they may stand
            in. See LandingRoute. */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <LandingRoute />
            </RequireAuth>
          }
        />
        {/* Declared before "/products/:productId" so this reads as an action
            rather than as a product whose id happens to be "add". */}
        <Route
          path="/products/add"
          element={
            <RequireAuth>
              <RequireCapability capability={CAP.productsWrite} title="Add Product">
                <AddProductPage />
              </RequireCapability>
            </RequireAuth>
          }
        />
        <Route
          path="/products"
          element={
            <RequireAuth>
              <RequireCapability capability={CAP.productsRead} title="Products">
                <ProductManagementPage />
              </RequireCapability>
            </RequireAuth>
          }
        />
        {/* Declared before "/products/:productId" so the trailing word reads as
            the action being taken, not as part of the product's id.

            There is no "/products/:productId/delete": nothing in this
            application hard-deletes a product, and archiving is a lifecycle
            transition owned by LifecycleActions inside the workspace rather
            than a screen of its own. */}
        <Route
          path="/products/:productId/edit"
          element={
            <RequireAuth>
              <RequireCapability capability={CAP.productsWrite} title="Edit product">
                <EditProductPage />
              </RequireCapability>
            </RequireAuth>
          }
        />
        <Route
          path="/products/:productId"
          element={
            <RequireAuth>
              <RequireCapability capability={CAP.productsRead} title="Product">
                <ProductWorkspacePage mode="edit" />
              </RequireCapability>
            </RequireAuth>
          }
        />
        {/* Orders. Behind the same session gate as everything else here: an
            order carries a customer's name, phone and address, so there is no
            reading of one without an administrator session.

            There is no "/orders/:orderId/cancel" and no route per transition —
            moving an order is an action taken on the order from its own screen,
            decided by what the backend says is legal, not a place to navigate
            to. */}
        <Route
          path="/orders"
          element={
            <RequireAuth>
              <RequireCapability capability={CAP.ordersRead} title="Orders">
                <OrdersPage />
              </RequireCapability>
            </RequireAuth>
          }
        />
        <Route
          path="/orders/:orderId"
          element={
            <RequireAuth>
              <RequireCapability capability={CAP.ordersRead} title="Order">
                <OrderDetailPage />
              </RequireCapability>
            </RequireAuth>
          }
        />
        {/* Inventory. Behind the same session gate as everything else.

            ONE route, and no "/inventory/:itemId": movement history expands
            inside the row it belongs to, because checking why a count looks
            wrong is done while scanning the list and a round trip to another
            screen loses the operator's place in it. There is no route per
            action either — adjusting stock is something done TO an item from
            the list, not a place to navigate to. */}
        <Route
          path="/inventory"
          element={
            <RequireAuth>
              <RequireCapability capability={CAP.inventoryRead} title="Inventory">
                <InventoryPage />
              </RequireCapability>
            </RequireAuth>
          }
        />
        {/* Discounts. ONE route and no sub-routes: a discount is one column on
            a product's working revision, so there is no discount to navigate
            to — setting and clearing one are things done TO a product from the
            list, and inspecting or publishing the result is the product's own
            workspace. There is deliberately no "/discounts/:productId": a
            second product screen is what this section must not become. */}
        <Route
          path="/discounts"
          element={
            <RequireAuth>
              {/* Reading prices is reading the catalogue. SETTING one is a
                  product write, and that is gated on the controls themselves —
                  a viewer may look at what things cost. */}
              <RequireCapability capability={CAP.productsRead} title="Discounts">
                <DiscountsPage />
              </RequireCapability>
            </RequireAuth>
          }
        />
        {/* Bulk Products. One route and no sub-routes: the workbook is the
            editing surface, so there is no per-product screen here to navigate
            to — choosing a file, reading the preview and confirming are one
            decision made in one place. */}
        <Route
          path="/bulk-products"
          element={
            <RequireAuth>
              <RequireCapability capability={CAP.bulkRead} title="Bulk Products">
                <BulkProductsPage />
              </RequireCapability>
            </RequireAuth>
          }
        />
        {/* Users Management. Reading who has authority is `users.read`, which
            only an owner holds; every action inside it additionally needs
            `users.manage` and the acting owner's own password. There is no
            Audit route yet — that read model is its own phase. */}
        <Route
          path="/users"
          element={
            <RequireAuth>
              <RequireCapability capability={CAP.usersRead} title="Users">
                <UsersPage />
              </RequireCapability>
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </QueryClientProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AuthedApp />
    </AuthProvider>
  );
}
